import React, { useState } from "react";
import { THEME, awardXP } from "./state";
import { ADAPTIVE_QUESTIONS, pickAdaptive, TIER_LABEL } from "./adaptiveData";
import Portal from "./Portal";

// Adaptive quiz modal — continuous practice with dynamic difficulty
export default function AdaptivePractice({ onClose }) {
  const [tier, setTier] = useState(0);
  const [asked, setAsked] = useState([]);
  const [current, setCurrent] = useState(() => pickAdaptive(0, []));
  const [selected, setSelected] = useState(null);
  const [locked, setLocked] = useState(false);
  const [correctStreak, setCorrectStreak] = useState(0);
  const [wrongStreak, setWrongStreak] = useState(0);
  const [totals, setTotals] = useState({ correct: 0, wrong: 0 });
  const [notice, setNotice] = useState("");

  const choose = (i) => {
    if (locked) return;
    setSelected(i);
    setLocked(true);
    const isRight = i === current.q.correct;
    const nextAsked = [...asked, current.i];
    setAsked(nextAsked);
    setTotals((t) => ({ correct: t.correct + (isRight ? 1 : 0), wrong: t.wrong + (isRight ? 0 : 1) }));

    let newCorrect = correctStreak;
    let newWrong = wrongStreak;
    let newTier = tier;
    let message = "";

    if (isRight) {
      newCorrect = correctStreak + 1;
      newWrong = 0;
      if (newCorrect >= 3 && tier < 2) {
        newTier = tier + 1;
        newCorrect = 0;
        message = `🔥 You're on a streak! Leveling up to ${TIER_LABEL[newTier]}…`;
      }
    } else {
      newWrong = wrongStreak + 1;
      newCorrect = 0;
      if (newWrong >= 2 && tier > 0) {
        newTier = tier - 1;
        newWrong = 0;
        message = `Let's step back to ${TIER_LABEL[newTier]} and rebuild momentum.`;
      }
    }

    setCorrectStreak(newCorrect);
    setWrongStreak(newWrong);
    setNotice(message);

    setTimeout(() => {
      setTier(newTier);
      setCurrent(pickAdaptive(newTier, nextAsked));
      setSelected(null);
      setLocked(false);
    }, 1100);
  };

  const finish = () => {
    awardXP(Math.min(200, totals.correct * 20), "Adaptive practice");
    onClose && onClose();
  };

  return (
    <Portal>
    <div data-testid="adaptive-practice-modal" style={overlay}>
      <div className="flexx-slide-up" style={container}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div>
            <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace" }}>ADAPTIVE PRACTICE</div>
            <h3 style={{ color: THEME.text, margin: "6px 0 0", fontSize: 18 }}>AXEL is adjusting to you</h3>
          </div>
          <button aria-label="Close" data-testid="adaptive-practice-close-btn" onClick={finish} style={{ background: "transparent", border: "none", color: THEME.mute, fontSize: 22, cursor: "pointer" }}>×</button>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 16, margin: "12px 0 16px", flexWrap: "wrap" }}>
          <div data-testid="adaptive-difficulty-indicator" style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {[0, 1, 2].map((t) => (
              <span key={t} className={`flexx-diff-dot ${t <= tier ? "on" : "off"}`} />
            ))}
            <span style={{ color: THEME.text, fontSize: 12, fontWeight: 700, marginLeft: 6 }} data-testid="adaptive-tier-label">
              {TIER_LABEL[tier]}
            </span>
          </div>
          <div style={{ flex: 1, display: "flex", gap: 14, justifyContent: "flex-end", fontSize: 12, color: THEME.mute }}>
            <span>✓ <b style={{ color: THEME.green }}>{totals.correct}</b></span>
            <span>✗ <b style={{ color: THEME.red }}>{totals.wrong}</b></span>
          </div>
        </div>

        {notice && (
          <div data-testid="adaptive-difficulty-notice" className="flexx-fade-in" style={{ background: THEME.tealSoft, border: `1px solid ${THEME.tealEdge}`, color: THEME.text, padding: "8px 12px", borderRadius: 10, fontSize: 12, marginBottom: 12 }}>
            {notice}
          </div>
        )}

        <div style={{ color: THEME.mute, fontSize: 11, marginBottom: 8 }}>Question</div>
        <p style={{ color: THEME.text, fontSize: 15, lineHeight: 1.55, marginBottom: 14 }}>{current.q.q}</p>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {current.q.choices.map((c, i) => {
            const isSel = selected === i;
            const showCorrect = locked && i === current.q.correct;
            const showWrong = locked && isSel && i !== current.q.correct;
            const cls = ["flexx-quiz-option", isSel && !locked ? "selected" : "", showCorrect ? "correct" : "", showWrong ? "wrong flexx-shake" : ""].join(" ");
            return (
              <button
                key={i}
                data-testid={`adaptive-option-${i}`}
                disabled={locked}
                className={cls}
                onClick={() => choose(i)}
              >
                <span style={{ width: 22, height: 22, borderRadius: "50%", border: `1px solid ${THEME.border}`, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: THEME.mute }}>
                  {String.fromCharCode(65 + i)}
                </span>
                <span>{c}</span>
              </button>
            );
          })}
        </div>

        <div style={{ textAlign: "center", marginTop: 14 }}>
          <button className="flexx-btn-ghost" data-testid="adaptive-practice-finish-btn" onClick={finish}>
            End session & claim XP ({totals.correct * 20} XP)
          </button>
        </div>
      </div>
    </div>
    </Portal>
  );
}

const overlay = {
  position: "fixed", inset: 0, zIndex: 9800,
  background: "rgba(0,0,0,0.88)",
  display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
};
const container = {
  background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 18, padding: 24,
  maxWidth: 540, width: "100%", maxHeight: "92vh", overflowY: "auto",
};
